import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Modal from "@/components/Modal";
import { Plus, Edit2, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["pending", "in_transit", "delivered", "returned"];
const STATUS_META = {
  pending: { label: "Pending", bg: "#F9F8F6", accent: "#6B6965" },
  in_transit: { label: "In Transit", bg: "#FFF0E6", accent: "#C85A17" },
  delivered: { label: "Delivered", bg: "#EAF0EA", accent: "#2F4F4F" },
  returned: { label: "Returned", bg: "#F5EDED", accent: "#B91C1C" },
};
const EMPTY = { customer_id: "", customer_name: "", deal_id: "", carrier: "DHL", tracking_no: "", status: "pending", shipped_at: "", notes: "" };

export default function Couriers() {
  const [shipments, setShipments] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/shipments").then((r) => setShipments(r.data));
  useEffect(() => {
    load();
    api.get("/carriers").then((r) => setCarriers(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const openAdd = () => { setForm(EMPTY); setEditing(null); setModal("add"); };
  const openEdit = (s) => { setForm({ ...EMPTY, ...s }); setEditing(s); setModal("edit"); };

  const onCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setForm((f) => ({ ...f, customer_id: id, customer_name: c?.company || "" }));
  };

  const save = async () => {
    try {
      if (editing) await api.patch(`/shipments/${editing.id}`, form);
      else await api.post("/shipments", form);
      toast.success(editing ? "Shipment updated" : "Shipment added");
      setModal(null); load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this shipment?")) return;
    await api.delete(`/shipments/${id}`); toast.success("Deleted"); load();
  };

  const carrierOptions = carriers.length ? carriers : ["DHL", "Fedex", "Bluedart", "Aramex", "Airwings"];

  return (
    <div data-testid="couriers-page">
      <div className="flex justify-end mb-4">
        <button onClick={openAdd} className="btn-primary" data-testid="add-shipment-btn"><Plus size={15} /> Add Shipment</button>
      </div>
      {shipments.length === 0 ? (
        <div className="card-ledger" style={{ padding: 48, textAlign: "center", color: "var(--text-2)" }}>
          <Truck size={28} style={{ margin: "0 auto 12px" }} /> No shipments tracked yet.
        </div>
      ) : (
        <div className="card-ledger overflow-hidden">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "var(--surface-2)" }}>
              {["Customer", "Carrier", "Tracking #", "Status", "Shipped", ""].map((h, i) => (
                <th key={i} className="overline" style={{ fontSize: 10, padding: "12px 16px", textAlign: "left", borderBottom: "1px solid var(--border)" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {shipments.map((s) => {
                const m = STATUS_META[s.status] || STATUS_META.pending;
                return (
                  <tr key={s.id} style={{ borderBottom: "1px solid var(--border)" }} data-testid={`shipment-row-${s.id}`}>
                    <td style={{ padding: "14px 16px", fontWeight: 600, fontSize: 14 }}>{s.customer_name || "—"}</td>
                    <td style={{ padding: "14px 16px", fontSize: 13 }}>{s.carrier}</td>
                    <td style={{ padding: "14px 16px", fontSize: 13 }} className="font-mono-data">{s.tracking_no || "—"}</td>
                    <td style={{ padding: "14px 16px" }}>
                      <span className="tag" style={{ background: m.bg, borderColor: "var(--border)", color: m.accent }}>{m.label}</span>
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: 13, color: "var(--text-2)" }} className="font-mono-data">{s.shipped_at || "—"}</td>
                    <td style={{ padding: "14px 16px", textAlign: "right" }}>
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openEdit(s)} className="btn-ghost" title="Edit"><Edit2 size={14} /></button>
                        <button onClick={() => remove(s.id)} className="btn-ghost" style={{ color: "var(--danger)" }} title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal title={editing ? "Edit Shipment" : "Add Shipment"} onClose={() => setModal(null)} size="md">
          <div className="grid gap-4">
            <div>
              <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Customer</label>
              <select value={form.customer_id} onChange={(e) => onCustomer(e.target.value)} className="input-base" data-testid="shipment-customer">
                <option value="">— Select customer —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.company}</option>)}
              </select>
            </div>
            <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <div>
                <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Carrier</label>
                <select value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} className="input-base" data-testid="shipment-carrier">
                  {carrierOptions.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input-base" data-testid="shipment-status">
                  {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <div>
                <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Tracking #</label>
                <input value={form.tracking_no} onChange={(e) => setForm({ ...form, tracking_no: e.target.value })} className="input-base" data-testid="shipment-tracking" />
              </div>
              <div>
                <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Shipped Date</label>
                <input type="date" value={form.shipped_at} onChange={(e) => setForm({ ...form, shipped_at: e.target.value })} className="input-base font-mono-data" />
              </div>
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Notes</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="input-base" style={{ fontFamily: "inherit" }} />
            </div>
            <div className="flex gap-3 justify-end mt-2">
              <button onClick={() => setModal(null)} className="btn-outline">Cancel</button>
              <button onClick={save} className="btn-primary" data-testid="shipment-save">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
